import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import styles from './LoyaltyPage.module.css';

export default function LoyaltyPage() {
  const [settings, setSettings]       = useState(null);
  const [partners, setPartners]       = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState('');
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const [showForm, setShowForm]       = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [form, setForm] = useState({ name: '', logoUrl: '', offerText: '', pointsNeeded: 500, isActive: true, sortOrder: 0 });

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [s, p, l] = await Promise.all([
        adminAPI.getLoyaltySettings(),
        adminAPI.getLoyaltyPartners(),
        adminAPI.getLoyaltyLeaderboard(),
      ]);
      if (s.settings)    setSettings(s.settings);
      if (p.partners)    setPartners(p.partners);
      if (l.leaderboard) setLeaderboard(l.leaderboard);
    } catch (err) {
      setError('Failed to load loyalty data. Make sure the backend is running.');
    } finally {
      setLoading(false);
    }
  }

  async function saveSettings(e) {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    try {
      const res = await adminAPI.saveLoyaltySettings({
        pointsPerDollarOnline: parseFloat(settings.points_per_dollar_online),
        pointsPerDollarStore:  parseFloat(settings.points_per_dollar_store),
        minRedeemPoints:       parseInt(settings.min_redeem_points),
        isActive:              settings.is_active,
      });
      if (res.settings) { setSettings(res.settings); setMsg('Settings saved.'); }
    } catch {
      setMsg('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  }

  function openAdd() {
    setEditPartner(null);
    setForm({ name: '', logoUrl: '', offerText: '', pointsNeeded: 500, isActive: true, sortOrder: partners.length });
    setShowForm(true);
  }

  function openEdit(p) {
    setEditPartner(p);
    setForm({ name: p.name, logoUrl: p.logo_url || '', offerText: p.offer_text, pointsNeeded: p.points_needed, isActive: p.is_active, sortOrder: p.sort_order });
    setShowForm(true);
  }

  async function savePartner(e) {
    e.preventDefault();
    try {
      if (editPartner) {
        await adminAPI.updatePartner(editPartner.id, form);
      } else {
        await adminAPI.createPartner(form);
      }
      setShowForm(false);
      load();
    } catch (err) {
      console.error('Save partner error:', err);
    }
  }

  async function deletePartner(id) {
    if (!window.confirm('Delete this partner?')) return;
    await adminAPI.deletePartner(id);
    load();
  }

  async function togglePartner(p) {
    await adminAPI.updatePartner(p.id, { isActive: !p.is_active });
    load();
  }

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading loyalty data…</div>;
  if (error)   return <div style={{ padding: 48, textAlign: 'center', color: 'var(--danger)' }}>{error}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Loyalty Program</h1>
        <p className={styles.subtitle}>Manage points settings, partner offers, and view the leaderboard</p>
      </div>

      <div className={styles.grid}>
        {/* ── Points Settings ── */}
        {settings && (
          <div className="card">
            <h2 className={styles.cardTitle}>Points Settings</h2>
            <form onSubmit={saveSettings}>
              <div className={styles.field}>
                <label className={styles.label}>Points per $1 — Online orders</label>
                <input type="number" step="0.1" min="0" className={styles.input}
                  value={settings.points_per_dollar_online}
                  onChange={e => setSettings({ ...settings, points_per_dollar_online: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Points per $1 — In-store / QR</label>
                <input type="number" step="0.1" min="0" className={styles.input}
                  value={settings.points_per_dollar_store}
                  onChange={e => setSettings({ ...settings, points_per_dollar_store: e.target.value })} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Minimum points to redeem</label>
                <input type="number" min="1" className={styles.input}
                  value={settings.min_redeem_points}
                  onChange={e => setSettings({ ...settings, min_redeem_points: e.target.value })} />
              </div>
              <div className={styles.fieldRow}>
                <label className={styles.label}>Program active</label>
                <label className={styles.toggle}>
                  <input type="checkbox" checked={settings.is_active}
                    onChange={e => setSettings({ ...settings, is_active: e.target.checked })} />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
              <button className="btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
              {msg && <p className={msg.includes('Failed') ? styles.msgErr : styles.msgOk}>{msg}</p>}
            </form>
          </div>
        )}

        {/* ── Partner Offers ── */}
        <div className="card">
          <div className={styles.cardTitleRow}>
            <h2 className={styles.cardTitle}>Partner Offers</h2>
            <button className="btn-primary" onClick={openAdd}>+ Add Partner</button>
          </div>

          {partners.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-text">No partner offers yet</div>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Brand</th>
                  <th>Offer</th>
                  <th>Points</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {partners.map(p => (
                  <tr key={p.id}>
                    <td>
                      {p.logo_url
                        ? <img src={p.logo_url} alt={p.name} className={styles.logoThumb} />
                        : <div className={styles.logoPlaceholder}>{p.name.charAt(0)}</div>
                      }
                    </td>
                    <td><strong>{p.name}</strong></td>
                    <td>{p.offer_text}</td>
                    <td>{p.points_needed.toLocaleString()} pts</td>
                    <td>
                      <span
                        className={`badge ${p.is_active ? 'badge-active' : 'badge-inactive'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => togglePartner(p)}
                        title="Click to toggle"
                      >
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-ghost" onClick={() => openEdit(p)}>Edit</button>
                        <button className="btn-danger" onClick={() => deletePartner(p.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Leaderboard ── */}
      <div className="card" style={{ marginTop: 24 }}>
        <h2 className={styles.cardTitle}>Points Leaderboard (Top 50)</h2>
        {leaderboard.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-text">No loyalty activity yet</div>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Total Points</th>
                <th>Orders</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((u, i) => (
                <tr key={u.id}>
                  <td><strong>{i + 1}</strong></td>
                  <td>{u.first_name} {u.last_name}</td>
                  <td>{u.mobile}</td>
                  <td><strong>{Number(u.total_points).toLocaleString()}</strong></td>
                  <td>{u.total_orders}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Partner Form Modal ── */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>{editPartner ? 'Edit Partner' : 'Add Partner'}</h3>
            <form onSubmit={savePartner}>
              <div className={styles.field}>
                <label className={styles.label}>Brand Name *</label>
                <input className={styles.input} required value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. King Jouet" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Logo URL</label>
                <input className={styles.input} value={form.logoUrl}
                  onChange={e => setForm({ ...form, logoUrl: e.target.value })}
                  placeholder="https://example.com/logo.png" />
                {form.logoUrl && (
                  <img src={form.logoUrl} alt="preview" className={styles.logoPreview}
                    onError={e => e.target.style.display = 'none'} />
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Offer Text *</label>
                <input className={styles.input} required value={form.offerText}
                  onChange={e => setForm({ ...form, offerText: e.target.value })}
                  placeholder="e.g. 10% OFF your next purchase" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Points Required *</label>
                <input className={styles.input} type="number" min="1" required value={form.pointsNeeded}
                  onChange={e => setForm({ ...form, pointsNeeded: parseInt(e.target.value) })} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Sort Order</label>
                <input className={styles.input} type="number" min="0" value={form.sortOrder}
                  onChange={e => setForm({ ...form, sortOrder: parseInt(e.target.value) })} />
              </div>
              <div className={styles.fieldRow}>
                <label className={styles.label}>Active</label>
                <label className={styles.toggle}>
                  <input type="checkbox" checked={form.isActive}
                    onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
              <div className={styles.modalActions}>
                <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save Partner</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
